import * as React from 'react';
import { connect } from 'react-redux';
import { OrderedMap } from 'immutable';
import cx from 'classnames';

import ErrorWithCode from 'lib/ErrorWithCode';
import { getDefaultScheduledTime } from 'lib/SchedulingMessage';
import { putMessageRecipientsForTargets } from 'lib/RoleBasedMessagingUtils';

import Button from 'components/ButtonDeprecated';
import Buttons from 'components/Buttons';
import CharacterCount from 'lib/CharacterCount';
import Composer from 'chat/components/Composer';
import CurrentUser from 'auth/CurrentUser';
import errorLogger from 'utils/errorLogger';
import EventTracking from 'lib/EventTracking';
import Glyph from 'components/Glyph';
import Message from 'entities/Message';
import RemainingCharactersCount from 'components/RemainingCharactersCount';
import Text from 'components/Text';
import ToastActions from 'actions/ToastActions';
import SimpleTranslateButton from 'components/SimpleTranslateButton';
import VisualComposerMessageActions from 'chat/actions/VisualComposerMessageActions';
import VisualComposerPrimaryButton from './ShareSDKVisualComposerPrimaryButton';
import VisualComposerScheduler from './ShareSDKVisualComposerScheduler';
import BlockedContentDialog from 'chat/components/_shared/BlockedContentDialog';

import { Target } from 'entities/Message';
import { ContentSourcesContent_me_composer_contentSourcesContent_items_downloadPreview as DownloadPreview } from 'user/queries/__generated__/ContentSourcesContent';

import 'chat/components/styles/VisualComposer.scss';
import { ComposerContext, UIEventContext } from './ShareSDKComposerContext';
import { MessageTranslationsInput } from 'globalTypes';
import { UploadRequestHandler, ThumbnailHandler } from 'lib/fileUploadHelper';

type ComposerLinkPreviewType = {
  title: string;
  description: string;
  url: string;
  image: string;
  image_width: string;
  image_height: string;
  rich_content_type: string;
  rich_content_url: string;
  downloadPreview?: DownloadPreview;
};

type Props = {
  autoFocus?: boolean;
  className?: string | null;
  charLimit: number;
  charLimitWithFiles: number;
  constrainMaxHeight?: boolean;
  districtIsSelected?: boolean;
  initialMessage: Message;
  isEditingScheduledMessage?: boolean;
  onMessageSent: (targets: Target[]) => void;
  targets: Target[];
  onBack: () => void;
  orgUuid?: string;
  orgName?: string;
  onRequestHide?: () => void;
  onTextChange: (text: string) => void;
  onScheduledTimeChange: (date?: Date | null) => void;
  text: string;
  scheduledTime?: Date;
  uuidForNewMessage?: string;
  linkPreviews?: OrderedMap<string, ComposerLinkPreviewType> | null;
  isUrgentMessage?: boolean;
  translations?: MessageTranslationsInput;
  filePreview?: React.ReactNode;
  fileUuid?: string;
  fileUuids?: string[];
  uploadRequest: UploadRequestHandler;
  loadThumbnail: ThumbnailHandler;
} & ReduxStateProps;

type ReduxStateProps = {
  totalRecipientCount: number;
};

type State = {
  schedulerOpened: boolean;
  latestTranslation?: string;
  blockedContentError?: string;
};

export class VisualComposer extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    const { scheduledTime } = props;
    this.state = {
      schedulerOpened: !!scheduledTime,
    };
  }

  handleTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = event.currentTarget.value;
    const { onTextChange } = this.props;
    onTextChange(text);
  };

  handleToggleScheduler = () => {
    const { onScheduledTimeChange } = this.props;
    const { schedulerOpened } = this.state;
    const scheduledTime = schedulerOpened ? null : getDefaultScheduledTime();

    this.setState({
      schedulerOpened: !schedulerOpened,
    });

    onScheduledTimeChange(scheduledTime);
  };

  handleDatePickerChanged = (inputDate?: Date | null) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime && inputDate) {
      const oldDate = new Date(scheduledTime.getTime());
      inputDate.setHours(oldDate.getHours());
      inputDate.setMinutes(oldDate.getMinutes());
      onScheduledTimeChange(inputDate);
    }
  };

  handleTimePickerChanged = (inputDate?: Date | null) => {
    const { scheduledTime, onScheduledTimeChange } = this.props;

    if (scheduledTime && inputDate) {
      const newDate = new Date(scheduledTime.getTime());
      newDate.setHours(inputDate.getHours());
      newDate.setMinutes(inputDate.getMinutes());
      onScheduledTimeChange(newDate);
    }
  };

  handleMessageSent = () => {
    const { onTextChange, onMessageSent, targets } = this.props;
    onTextChange('');
    onMessageSent(targets);
  };

  handleMessageSentError = (error: Error) => {
    if (error instanceof ErrorWithCode && error.code === 'blocked_content') {
      this.setState({ blockedContentError: error.message });
    } else {
      ToastActions.showAPIError(error);
    }
    const dwmPrefix = this.props.districtIsSelected ? 'DWM - ' : '';
    console.warn(error);
    console.error(`${dwmPrefix}VisualComposer sendMessage failed`);
  };

  putMessageInputFromState(senderType: string, senderUuid: string) {
    const {
      targets,
      text,
      fileUuid,
      fileUuids: receivedFileUuids,
      uuidForNewMessage,
      linkPreviews,
      isUrgentMessage,
    } = this.props;
    const recipients = putMessageRecipientsForTargets(targets);
    const fileUuids = receivedFileUuids || [];
    if (fileUuid && fileUuids.length === 0) {
      fileUuids.push(fileUuid);
    }
    const arPreviews = linkPreviews ? linkPreviews.toList().toArray() : [];
    const linkPreviewsToSend = arPreviews.map((preview) => {
      return {
        url: preview.url,
        title: preview.title,
        description: preview.description,
        image: preview.image,
        image_width: preview.image_width ? String(preview.image_width) : null,
        image_height: preview.image_height ? String(preview.image_height) : null,
        rich_content_type: preview.rich_content_type,
        rich_content_url: preview.rich_content_url,
      };
    });
    return {
      recipients,
      message: {
        body: text,
        fileUuids,
        linkPreviews: linkPreviewsToSend,
        urgent: isUrgentMessage || false,
      },
      uuid: uuidForNewMessage,
      sender: {
        type: senderType,
        uuid: senderUuid,
      },
    };
  }

  sendMessage = (propagateThrow: boolean = false): Promise<void> => {
    const {
      isEditingScheduledMessage,
      scheduledTime,
      uuidForNewMessage,
      districtIsSelected,
      orgUuid,
    } = this.props;

    const promiseHandler = (
      promise: Promise<void>,
      errMsg = 'VisualComposer sendMessage failed'
    ) => {
      return promise.then(this.handleMessageSent, this.handleMessageSentError).catch((err) => {
        errorLogger(errMsg)(err);
        return propagateThrow ? Promise.reject(err) : undefined;
      });
    };

    const putMessageInput = this.putMessageInputFromState('user', CurrentUser.getUserUuid());
    if (scheduledTime) {
      if (isEditingScheduledMessage) {
        // Do we really update scheduled messages through the SDK????
        const updateScheduledMessagePromise = VisualComposerMessageActions.updateScheduledMessage({
          uuid: uuidForNewMessage ?? '',
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: scheduledTime as unknown as string,
          },
        });
        return promiseHandler(updateScheduledMessagePromise as unknown as Promise<void>);
      } else {
        const scheduledMessagePromise = VisualComposerMessageActions.scheduleMessage({
          input: {
            messageInput: putMessageInput,
            // for some reason GQL expects a string, but we've always sent a Date
            sendAt: scheduledTime as unknown as string,
          },
        });
        return promiseHandler(scheduledMessagePromise as unknown as Promise<void>);
      }
    } else if (districtIsSelected) {
      if (!orgUuid) {
        // just making sure this never causes unhandled promises.
        return propagateThrow ? Promise.reject() : Promise.resolve();
      }
      const districtPutMessageInput = this.putMessageInputFromState('district', orgUuid);
      const putDWMMessagePromise = VisualComposerMessageActions.putMessage({
        input: districtPutMessageInput,
      });
      return promiseHandler(
        putDWMMessagePromise as unknown as Promise<void>,
        'DWM - VisualComposer sendMessage promise failed'
      );
    } else {
      const putMessagePromise = VisualComposerMessageActions.putMessage({ input: putMessageInput });
      return promiseHandler(putMessagePromise as unknown as Promise<void>);
    }
  };

  isValidMessage = () => {
    const { filePreview, fileUuid, targets, text } = this.props;

    const isAddressed = targets && targets.length > 0;

    let uploadComplete = false;
    // Beware! sadly we made a mess of linkPreviews and typescript, so validating it is awkward to say the least
    // If filePreview, check if it has the props we expect to know if it has loaded
    if (
      filePreview &&
      React.isValidElement(filePreview) &&
      filePreview.props &&
      filePreview.props.previews &&
      filePreview.props.previews.length &&
      filePreview.props.previews[0]
    ) {
      uploadComplete = !!filePreview.props.previews[0].linkPreview?.url;
    }
    // If no filePreview, check if fileUuid is set meaning the file was fully uploaded
    else {
      uploadComplete = filePreview ? !!fileUuid : true;
    }

    const hasText = text.trim().length > 0;

    return isAddressed && uploadComplete && (hasText || !!fileUuid);
  };

  isSubmitDisabled = (charactersRemaining: number) => {
    return charactersRemaining < 0 || !this.isValidMessage();
  };

  renderFooterPanel(charactersRemaining: number, uiEventContext: UIEventContext) {
    const { text, onTextChange } = this.props;
    return (
      <div className="VisualComposer-footerPanel">
        <span className="VisualComposer-footerPanel-actions">
          <SimpleTranslateButton
            onTextChange={onTextChange}
            onLangChange={(lang) => {
              this.setState({
                latestTranslation: lang,
              });
            }}
            eventKey="announcements.announcement_modal.universal_composer.show_translation_box.click"
            eventProperties={uiEventContext}
            dataTestSelector="visual_composer_translation_button"
            textToTranslate={text}
          />
        </span>
        <div className="VisualComposer-remainingCharacterCount">
          <RemainingCharactersCount count={charactersRemaining} />
        </div>
      </div>
    );
  }

  renderDropzone() {
    return (
      <div className="VisualComposer-dropzone">
        <Glyph
          className="VisualComposer-dropzone-icon"
          color="graphite"
          name="drag-and-drop"
          size="xlarge"
        />
        <Text className="VisualComposer-dropzone-text" color="graphite" size="xxxsmall">
          Drag and drop here
        </Text>
      </div>
    );
  }

  render() {
    const {
      charLimit,
      charLimitWithFiles,
      constrainMaxHeight,
      autoFocus,
      filePreview,
      fileUuid,
      text,
      districtIsSelected,
      isUrgentMessage,
      orgUuid,
      onRequestHide,
      totalRecipientCount,
      targets,
      orgName,
    } = this.props;
    const { schedulerOpened, latestTranslation, blockedContentError } = this.state;

    const charactersRemaining = CharacterCount.getRemainingCharactersForText(
      charLimit,
      charLimitWithFiles,
      text.trim(),
      !!fileUuid
    );

    const fileSection = !filePreview ? this.renderDropzone() : filePreview;

    return (
      <ComposerContext.Consumer>
        {(composerContext) => (
          <div
            className={cx('VisualComposer', this.props.className)}
            data-test-selector="visual_composer_textarea"
          >
            {blockedContentError && (
              <BlockedContentDialog
                onRequestHide={() => this.setState({ blockedContentError: undefined })}
                errorMessage={blockedContentError}
              />
            )}
            <div className="VisualComposer-composerWrapper">
              <div className="VisualComposer-wrap">
                <div className="VisualComposer-composerContainer">
                  <Composer
                    className="VisualComposer-growingComposer"
                    placeholder={'Type your message'}
                    autogrow
                    constrainMaxHeight={constrainMaxHeight}
                    value={text}
                    autoFocus={autoFocus}
                    error={charactersRemaining < 0}
                    onChange={this.handleTextChange}
                  >
                    {this.renderFooterPanel(
                      charactersRemaining,
                      composerContext.getUIEventContext()
                    )}
                    {fileSection}
                  </Composer>
                </div>
                <div className="VisualComposer-buttonContainer">
                  {schedulerOpened ? (
                    <span />
                  ) : (
                    <Button
                      level="secondary"
                      weight="normal"
                      onClick={() => {
                        EventTracking.trackKey(
                          'announcements.share_to.back.click',
                          composerContext.getUIEventContext()
                        );
                        this.props.onBack();
                      }}
                    >
                      Back
                    </Button>
                  )}
                  <Buttons>
                    <VisualComposerScheduler
                      scheduledTime={this.props.scheduledTime}
                      schedulerOpened={schedulerOpened}
                      targets={targets}
                      districtIsSelected={districtIsSelected}
                      onToggleScheduler={this.handleToggleScheduler}
                      onDatePickerChanged={this.handleDatePickerChanged}
                      onTimePickerChanged={this.handleTimePickerChanged}
                      isEditingScheduledMessage={this.props.isEditingScheduledMessage}
                    />
                    <VisualComposerPrimaryButton
                      charactersRemaining={charactersRemaining}
                      districtIsSelected={districtIsSelected}
                      isScheduling={schedulerOpened}
                      isUrgentMessage={isUrgentMessage}
                      latestTranslation={latestTranslation}
                      messageIsValid={this.isValidMessage()}
                      uiEventContext={composerContext.getUIEventContext()}
                      onContinueConfirm={() => {
                        this.sendMessage(true)
                          .then(() => {
                            EventTracking.trackKey(
                              'announcements.composer.confirm_send.send.click',
                              {
                                ...composerContext.getUIEventContext(),
                                organization_uuid: orgUuid,
                              }
                            );
                          })
                          .catch(() => {
                            console.error(
                              `${
                                isUrgentMessage ? 'URGENT' : 'DWM'
                              } - missing district uuid - could not write district message`
                            );
                          });
                        if (onRequestHide) {
                          onRequestHide();
                        }
                      }}
                      onSchedule={() => this.sendMessage()}
                      onSend={() => this.sendMessage()}
                      orgName={orgName}
                      orgUuid={orgUuid}
                      targets={targets}
                      totalRecipientCount={totalRecipientCount}
                    />
                  </Buttons>
                </div>
              </div>
            </div>
          </div>
        )}
      </ComposerContext.Consumer>
    );
  }
}

const mapStateToProps = (state: any): ReduxStateProps => {
  return {
    totalRecipientCount: state.roleBasedMessagingSearch.totalRecipientCount,
  };
};

export default connect(mapStateToProps)(VisualComposer);
